import React, { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { enqueueSnackbar } from 'notistack';

export const SALES_EMAIL = 'info@bitedata.io';
const SUBJECT = 'Business verification';
const BODY =
  'Hi, we would like to add business verification to our team’s plan. Please get in touch.';

const query = (params: Record<string, string>) =>
  Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

/**
 * Every way to start an email to sales. A bare mailto: link does nothing on a
 * machine with no mail app set up, which is common for webmail users, so the
 * webmail composers and a copy of the address sit beside it.
 */
export const SALES_EMAIL_COMPOSERS = {
  gmail: `https://mail.google.com/mail/?${query({
    view: 'cm',
    fs: '1',
    to: SALES_EMAIL,
    su: SUBJECT,
    body: BODY,
  })}`,
  outlook: `https://outlook.office.com/mail/deeplink/compose?${query({
    to: SALES_EMAIL,
    subject: SUBJECT,
    body: BODY,
  })}`,
  mailApp: `mailto:${SALES_EMAIL}?${query({ subject: SUBJECT, body: BODY })}`,
};

interface ContactSalesMenuProps {
  className?: string;
}

const ContactSalesMenu: React.FC<ContactSalesMenuProps> = ({ className }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);

  const copyAddress = async () => {
    close();
    try {
      await navigator.clipboard.writeText(SALES_EMAIL);
      enqueueSnackbar(`Copied ${SALES_EMAIL}`, { variant: 'success' });
    } catch {
      // Clipboard access can be refused; the address is still worth showing.
      enqueueSnackbar(`Email us at ${SALES_EMAIL}`, { variant: 'info' });
    }
  };

  return (
    <>
      <button
        type='button'
        className={className}
        aria-haspopup='menu'
        aria-expanded={Boolean(anchor)}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        Contact sales
        <KeyboardArrowDownOutlinedIcon aria-hidden='true' />
      </button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: 'contact-sales-menu' }}
      >
        <MenuItem
          component='a'
          href={SALES_EMAIL_COMPOSERS.gmail}
          target='_blank'
          rel='noopener noreferrer'
          onClick={close}
        >
          <OpenInNewOutlinedIcon aria-hidden='true' />
          Email in Gmail
        </MenuItem>
        <MenuItem
          component='a'
          href={SALES_EMAIL_COMPOSERS.outlook}
          target='_blank'
          rel='noopener noreferrer'
          onClick={close}
        >
          <OpenInNewOutlinedIcon aria-hidden='true' />
          Email in Outlook
        </MenuItem>
        <MenuItem
          component='a'
          href={SALES_EMAIL_COMPOSERS.mailApp}
          onClick={close}
        >
          <MailOutlineOutlinedIcon aria-hidden='true' />
          Open my mail app
        </MenuItem>
        <MenuItem onClick={copyAddress}>
          <ContentCopyOutlinedIcon aria-hidden='true' />
          Copy {SALES_EMAIL}
        </MenuItem>
      </Menu>
    </>
  );
};

export default ContactSalesMenu;
